import { useCallback, useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import ApiHttp from "byzantine/src/ApiHttp";
import { LIVE_LEDGER_ENRICHMENT_PARAMS } from "byzantine/src/TransactionEnrichmentSource";

export interface TransactionSubCategory {
  slug: string;
  label: string;
}

export interface TransactionCategory {
  slug: string;
  label: string;
  sub_categories: TransactionSubCategory[];
}

export interface TransactionCategoryTree {
  account_type: "consumer" | "business";
  /** "mx" trees are filter options only, keyed by stored MX category. */
  source?: "mx" | "narmi";
  categories: TransactionCategory[];
}

export interface UseTransactionCategoriesResult {
  categories: TransactionCategory[];
  source: TransactionCategoryTree["source"];
  labelForSlug: (slug: string) => string;
  isLoading: boolean;
  error: string | null;
}

export interface UseTransactionCategoriesOptions {
  /** Fetch this tree instead of the caller's own. Omit for the caller's own type. */
  accountType?: "consumer" | "business";
  /** False skips the request entirely, leaving an empty tree. Defaults to true. */
  enabled?: boolean;
}

export const TRANSACTION_CATEGORIES_QUERY_KEY = "transactionCategories";

// An FI administrator can switch consumer accounts between MX and Narmi categories, so the
// tree cannot be cached for the whole session.
const STALE_AFTER_MS = 5 * 60_000;

const EMPTY: TransactionCategory[] = [];

const extractErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === "string") return error;
  return "An unexpected error occurred";
};

export const useTransactionCategories = ({
  accountType,
  enabled = true,
}: UseTransactionCategoriesOptions = {}): UseTransactionCategoriesResult => {
  const { data, isLoading, error } = useQuery<TransactionCategoryTree>({
    queryKey: [
      TRANSACTION_CATEGORIES_QUERY_KEY,
      accountType ?? null,
      LIVE_LEDGER_ENRICHMENT_PARAMS.enrichment_view,
    ],
    queryFn: () => {
      const params = new URLSearchParams(
        accountType ? { account_type: accountType } : {}
      );
      params.set(
        "enrichment_view",
        LIVE_LEDGER_ENRICHMENT_PARAMS.enrichment_view
      );
      return ApiHttp.fetch<TransactionCategoryTree>(
        `transaction_categories?${params.toString()}`
      );
    },
    enabled,
    staleTime: STALE_AFTER_MS,
    retry: false,
  });

  const categories = data?.categories ?? EMPTY;

  // Both levels go in one map. No slug is both a category and a sub-category within a tree, and a
  // response only ever carries one tree, so the lookup cannot be ambiguous.
  const labelBySlug = useMemo(() => {
    const labels = new Map<string, string>();
    categories.forEach((category) => {
      labels.set(category.slug, category.label);
      category.sub_categories.forEach((subCategory) => {
        labels.set(subCategory.slug, subCategory.label);
      });
    });
    return labels;
  }, [categories]);

  const labelForSlug = useCallback(
    (slug: string) => labelBySlug.get(slug) ?? slug,
    [labelBySlug]
  );

  return {
    categories,
    source: data?.source,
    labelForSlug,
    isLoading,
    error: error ? extractErrorMessage(error) : null,
  };
};
